import { useGetCustomFieldsToSave } from "PFApp/hooks/activity_form/use_custom_fields_to_save";
import { useActivityInvalidate } from "PFCore/hooks/queries/activity";
import { useCustomValueInvalidate } from "PFCore/hooks/queries/custom_fields/use_custom_value_invalidate";
import { useMatchesInvalidate } from "PFCore/hooks/queries/matches/use_matches_invalidate";
import { useErrorsGrowl } from "PFCore/hooks/use_errors_growl";
import { Activity, ActivityData, Experience, Importance, PureCustomValueCamelCased } from "PFTypes";
import { useTranslation } from "react-i18next";

import { useCustomValuePreviewContext } from "../../context/custom_value_preview_context";
import { addCustomField, editCustomField, removeCustomField } from "./activity_custom_value_footer.utils";
import { Footer, FooterProps } from "./footer";

type ActivityCustomValueFooterProps = {
  activity: Activity | undefined;
  customValue: PureCustomValueCamelCased | undefined;
  importance: Importance | null;
  requiredExperience: Experience | null;
  updateActivity: (activityData: ActivityData) => Promise<Activity | undefined>;
} & Omit<FooterProps, "saveNewLabel" | "onRemove" | "onSave" | "onSaveAndReopen">;

export const ActivityCustomValueFooter = ({
  activity,
  customValue,
  importance,
  requiredExperience,
  updateActivity,
  onClose,
  ...props
}: ActivityCustomValueFooterProps) => {
  const { t } = useTranslation("translation", { keyPrefix: "customValueSidepanel" });
  const growlError = useErrorsGrowl();
  const { getCustomFieldsToSave } = useGetCustomFieldsToSave();
  const { invalidate: invalidateActivities } = useActivityInvalidate();
  const { invalidate: invalidateMatches } = useMatchesInvalidate();
  const {
    invalidateCustomValueActivityConnection,
    removeCustomValueActivityConnection,
    invalidateCustomValueInsights
  } = useCustomValueInvalidate();
  const {
    isNew,
    customValueId,
    customType,
    activityId,
    onSave: customOnSave,
    onRemove: customOnRemove,
    onReopen
  } = useCustomValuePreviewContext();

  const invalidate = (removeFromCache: boolean = false) => {
    invalidateActivities([activityId!]);
    invalidateCustomValueInsights(customValueId!);
    invalidateMatches([activityId!]);
    if (removeFromCache) {
      removeCustomValueActivityConnection(customValueId!, activityId!);
    } else {
      invalidateCustomValueActivityConnection(customValueId!, activityId!);
    }
  };

  const handleRemove = () => {
    const taskCustomFields = removeCustomField(activity!.custom_fields, customValueId!, customType.id);
    if (customOnRemove) {
      customOnRemove(taskCustomFields);
      onClose();
      return;
    }

    updateActivity({
      custom_fields: getCustomFieldsToSave(taskCustomFields, activity!.profile)
    })
      .then(() => {
        growlError(t("removeSuccess", { value: customValue?.text }));
        onClose();
        setTimeout(() => {
          // Invalidate after isClosing is true, to avoid incorrect query call
          invalidate(true);
        }, 0);
      })
      .catch(() => {
        growlError(t("removeError", { value: customValue?.text }));
      });
  };

  const handleSave = (onSuccess?: VoidFunction) => {
    const taskCustomFields = isNew
      ? addCustomField(activity!.custom_fields, customType!, customValue!, importance!, requiredExperience!)
      : editCustomField(
          activity!.custom_fields,
          customValueId!,
          customType.id,
          importance!,
          requiredExperience!
        );

    if (customOnSave) {
      customOnSave(taskCustomFields);
      onSuccess ? onSuccess() : onClose();
      return;
    }

    updateActivity({ custom_fields: getCustomFieldsToSave(taskCustomFields, activity!.profile) })
      .then(() => {
        growlError(t("saveSuccess", { value: customValue?.text }));
        onSuccess ? onSuccess() : onClose();
        setTimeout(() => {
          // Invalidate after isClosing is true, to avoid incorrect query call
          invalidate();
        }, 0);
      })
      .catch(() => {
        growlError(t("saveError", { value: customValue?.text }));
      });
  };

  const handleSaveAndReopen = () => {
    handleSave(onReopen);
  };

  return (
    <Footer
      {...props}
      onRemove={handleRemove}
      onSave={() => handleSave()}
      onSaveAndReopen={onReopen ? handleSaveAndReopen : undefined}
      onClose={onClose}
    />
  );
};
